import { supabase } from "@/integrations/supabase/client";
import { QueryOptions } from "./types";

// Define valid table names in two separate types to match Supabase's internal structure
// Tables from the database schema
type ValidTableName = 
  | "channels" 
  | "playlists" 
  | "clips" 
  | "downloads" 
  | "licenses" 
  | "derivatives" 
  | "profiles" 
  | "contact_submissions"
  | "challenge_applications";

// Views from the database schema
type ValidViewName =
  | "clips_with_source"
  | "derivatives_with_details"
  | "admin_user_view"
  | "user_derivative_counts";

// Combined type for any valid relation
export type ValidRelationName = ValidTableName | ValidViewName;

export class QueryBuilder {
  private table: ValidRelationName;
  private query: any;

  constructor(table: ValidRelationName) {
    this.table = table;
    
    // Handle tables and views with different type overloads
    if (this.isTableName(table)) {
      this.query = supabase.from(table);
    } else if (this.isViewName(table)) {
      this.query = supabase.from(table);
    } else {
      // This case should never happen due to TypeScript's type checking,
      // but we include it for runtime safety
      throw new Error(`Invalid table or view name: ${table}`);
    }
  }

  // Type guards to help TypeScript understand our types
  private isTableName(name: ValidRelationName): name is ValidTableName {
    const tables: ValidTableName[] = [
      "channels", "playlists", "clips", "downloads", 
      "licenses", "derivatives", "profiles", "contact_submissions",
      "challenge_applications"
    ];
    return tables.includes(name as ValidTableName);
  }

  private isViewName(name: ValidRelationName): name is ValidViewName {
    const views: ValidViewName[] = [
      "clips_with_source", "derivatives_with_details", 
      "admin_user_view", "user_derivative_counts"
    ];
    return views.includes(name as ValidViewName);
  }

  select(fields: string | Record<string, any>) {
    if (typeof fields === 'string') {
      this.query = this.query.select(fields);
    } else {
      const selectString = this.buildSelectString(fields);
      this.query = this.query.select(selectString);
    }
    return this;
  }

  private buildSelectString(fields: Record<string, any>, prefix = ''): string {
    return Object.entries(fields)
      .map(([key, value]) => {
        if (typeof value === 'object') {
          return `${key}(${this.buildSelectString(value)})`;
        }
        return prefix ? `${prefix}.${key}` : key;
      })
      .join(', ');
  }

  filter(column: string, operator: string, value: any) {
    this.query = this.query.filter(column, operator, value);
    return this;
  }

  eq(column: string, value: any) {
    this.query = this.query.eq(column, value);
    return this;
  }

  limit(count: number) {
    this.query = this.query.limit(count);
    return this;
  }

  order(column: string, options: { ascending?: boolean } = {}) {
    this.query = this.query.order(column, options);
    return this;
  }

  async execute() {
    const { data, error } = await this.query;
    if (error) {
      console.error(`Error executing query for ${this.table}:`, error);
      return null;
    }
    return data;
  }
}
